import { getValues } from "../designer-context";
import type { IManagedGuardContext } from "../managed-task-screen-guard";
import type { OwnershipDictionary } from "../managed-task-screen-ownership";
import { getSdInstance } from "./helpers";

/** The two places the guard's inputs actually live on the designer instance. */
interface ISdFlatState {
  project?: IManagedGuardContext["project"];
  sdId?: string;
}

interface ISdWithScreenProvider {
  screenProvider?: { state?: { screenDesignDictionary?: OwnershipDictionary } };
}

/**
 * Resolve everything `checkManagedTaskScreenOperation` reads, or null when the
 * designer state is not available at all.
 *
 * `getSdInstance()` returns the raw provider: `project` / `sdId` live on the FLAT
 * state behind `getValues`, while the component dictionary hangs off the
 * instance's `screenProvider`. Reading `sd.project` / `sd.sdId` straight off the
 * instance yields `undefined` and silently disables the guard — which is exactly
 * what happened live on 2026-07-27. Resolving both here, once, keeps that mistake
 * out of every call site.
 *
 * Returning null (rather than an empty context) is meaningful: no designer state
 * is not evidence of a managed screen, so the guard passes. An unreadable
 * DICTIONARY on a screen we can already tell is managed is a different case, and
 * the guard fails closed on it.
 */
export function resolveManagedGuardContext(): IManagedGuardContext | null {
  try {
    const sd: unknown = getSdInstance();
    if (sd === null || sd === undefined) return null;

    const state: ISdFlatState = (getValues(sd as never) ?? {}) as ISdFlatState;
    const dict: OwnershipDictionary | undefined = (sd as ISdWithScreenProvider).screenProvider?.state
      ?.screenDesignDictionary;

    if (state.project === undefined && dict === undefined) return null;

    return {
      ...(state.project !== undefined ? { project: state.project } : {}),
      ...(typeof state.sdId === "string" ? { screenId: state.sdId } : {}),
      ...(dict !== undefined ? { dict } : {})
    };
  } catch {
    // No designer state this tick — not evidence of a managed screen.
    return null;
  }
}
